import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  useGetCourseBySlugQuery,
  useGetCourseResultQuery,
  useGetStudentCourseResultQuery,
} from "@/features/api/courseApi";

import {
  User,
  Award,
  History,
  Loader2,
  BarChart3,
  Trophy,
  TrendingUp,
  AlertTriangle,
  Clock,
  RotateCcw,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  ReferenceLine,
} from "recharts";

export default function CourseResult() {
  const { courseSlug, studentId } = useParams();
  const navigate = useNavigate();

  const isAdminView = Boolean(studentId);

  /* ================= COURSE ================= */
  const { data: courseRes, isLoading: courseLoading } =
    useGetCourseBySlugQuery(courseSlug, { skip: !courseSlug });

  const course = courseRes?.course;
  const PASS_PERCENT = Number(course?.passPercentage) || 30;

  /* ================= RESULT ================= */
  const {
    data: rawResult,
    isLoading: resultLoading,
  } = isAdminView
    ? useGetStudentCourseResultQuery(
        { courseId: course?._id, studentId },
        { skip: !course?._id || !studentId }
      )
    : useGetCourseResultQuery(course?._id, {
        skip: !course?._id,
      });

  /* ================= DATA ================= */
  const attempts = rawResult?.attempts || [];
  const user = rawResult?.user || {};

  /* ================= SORT ================= */
  const sortedAttempts = useMemo(() => {
    return [...attempts].sort(
      (a, b) =>
        new Date(a.submittedAt || 0) -
        new Date(b.submittedAt || 0)
    );
  }, [attempts]);

  /* ================= STATS ================= */
  const stats = useMemo(() => {
    if (!sortedAttempts.length) {
      return { best: 0, avg: 0, latest: 0, passed: false };
    }

    const scores = sortedAttempts.map(
      (a) => Number(a.scorePercent) || 0
    );

    const avg =
      scores.reduce((a, b) => a + b, 0) / scores.length;

    const latest =
      sortedAttempts[sortedAttempts.length - 1]?.scorePercent || 0;

    return {
      best: Math.max(...scores),
      avg: Math.round(avg),
      latest,
      passed: latest >= PASS_PERCENT,
    };
  }, [sortedAttempts, PASS_PERCENT]);

  /* ================= CHART ================= */
  const attemptChart = useMemo(() => {
    return sortedAttempts.map((a, i) => ({
      name: `A${i + 1}`,
      score: Number(a.scorePercent) || 0,
    }));
  }, [sortedAttempts]);

  /* ================= SKILLS ================= */
  const skillData = useMemo(() => {
    const map = {};

    sortedAttempts.forEach((attempt) => {
      attempt.answers?.forEach((a) => {
        if (!a.skill) return;

        if (!map[a.skill]) {
          map[a.skill] = { total: 0, correct: 0 };
        }

        map[a.skill].total++;
        if (a.correct) map[a.skill].correct++;
      });
    });

    return Object.entries(map).map(([skill, v]) => ({
      skill,
      percent: v.total
        ? Math.round((v.correct / v.total) * 100)
        : 0,
    }));
  }, [sortedAttempts]);

  /* ================= INSIGHTS ================= */
  const insights = useMemo(() => {
    if (!sortedAttempts.length) return [];

    const list = [];

    if (stats.latest < PASS_PERCENT) {
      list.push("You need improvement to pass.");
    }

    if (stats.avg < 50) {
      list.push("Focus on fundamentals.");
    }

    if (stats.avg >= 75) {
      list.push("Excellent performance 🔥");
    }

    if (sortedAttempts.length >= 2) {
      const first = sortedAttempts[0]?.scorePercent || 0;
      const last = stats.latest;

      if (last > first) {
        list.push(`Improved by +${last - first}% 📈`);
      }
    }

    if (!list.length) list.push("Keep practicing 👍");

    return list;
  }, [sortedAttempts, stats, PASS_PERCENT]);

  const weakSkills = skillData.filter((s) => s.percent < 50);

  /* ================= STATES ================= */
  if (courseLoading || resultLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  if (!sortedAttempts.length) {
    return (
      <div className="text-center py-20">
        <p>No attempts yet</p>
        {!isAdminView && (
          <button
            onClick={() =>
              navigate(`/courses/${courseSlug}/learn`)
            }
            className="bg-blue-600 text-white px-6 py-2 rounded mt-4"
          >
            Start Learning
          </button>
        )}
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* ADMIN MODE */}
        {isAdminView && (
          <div className="text-center text-blue-600 font-medium">
            Admin View - Viewing Student Result
          </div>
        )}

        {/* HERO */}
        <div
          className={`p-8 rounded-2xl text-center shadow ${
            stats.passed ? "bg-green-50" : "bg-red-50"
          }`}
        >
          <h1 className="text-3xl font-bold">
            {course?.title}
          </h1>

          <p className="text-5xl font-bold mt-4">
            {stats.latest}%
          </p>

          <div className="mt-2 flex justify-center gap-2">
            {stats.passed ? (
              <>
                <CheckCircle2 className="text-green-600" /> Passed
              </>
            ) : (
              <>
                <XCircle className="text-red-600" /> Not Passed
              </>
            )}
          </div>
        </div>

        {/* STATS */}
        <div className="grid md:grid-cols-4 gap-4">
          <Stat icon={Trophy} label="Best" value={`${stats.best}%`} />
          <Stat icon={BarChart3} label="Average" value={`${stats.avg}%`} />
          <Stat icon={History} label="Attempts" value={sortedAttempts.length} />
          <Stat icon={Clock} label="Latest" value={`${stats.latest}%`} />
        </div>

        {/* CHART */}
        <Card title="Performance Trend" icon={TrendingUp}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={attemptChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <ReferenceLine y={PASS_PERCENT} strokeDasharray="4 4" />
              <Line
                dataKey="score"
                strokeWidth={3}
                stroke={stats.passed ? "#16a34a" : "#dc2626"}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* SKILLS */}
        <Card title="Skill Breakdown" icon={Award}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={skillData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="skill" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="percent" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* WEAK */}
        <Card title="Weak Areas" icon={AlertTriangle}>
          {weakSkills.length
            ? weakSkills.map((s) => (
                <p key={s.skill}>• {s.skill}</p>
              ))
            : "None 🎉"}
        </Card>

        {/* INSIGHTS */}
        <Card title="AI Insights" icon={TrendingUp}>
          {insights.map((i, idx) => (
            <p key={idx}>• {i}</p>
          ))}
        </Card>

        {/* TABLE */}
        <Card title="All Attempts" icon={History}>
          <table className="w-full text-left border">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3">#</th>
                <th>Date</th>
                <th>Score</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {sortedAttempts.map((a, i) => (
                <tr key={i} className="border-t">
                  <td className="p-3">{i + 1}</td>
                  <td>
                    {a.submittedAt
                      ? new Date(a.submittedAt).toLocaleString()
                      : "-"}
                  </td>
                  <td className="font-semibold">
                    {Number(a.scorePercent) || 0}%
                  </td>
                  <td>
                    {a.timeSpent
                      ? `${Math.floor(a.timeSpent / 60)} min`
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* USER */}
        <Card title="Student" icon={User}>
          <p>{user?.name || "N/A"}</p>
          <p className="text-gray-500">{user?.email}</p>
        </Card>

        {/* ACTION */}
        {!isAdminView && (
          <div className="flex justify-center">
            <button
              onClick={() =>
                navigate(`/courses/${courseSlug}/learn`)
              }
              className="bg-blue-600 text-white px-6 py-2 rounded flex gap-2 items-center"
            >
              <RotateCcw className="w-4 h-4" />
              Retake
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= UI ================= */

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow text-center">
      <Icon className="mx-auto mb-2 text-blue-600" />
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="flex items-center gap-2 font-semibold mb-4">
        <Icon size={18} className="text-blue-600" />
        {title}
      </h2>
      {children}
    </div>
  );
}